import React, { useEffect, useState, useMemo } from "react";
import "./App.css";
import Modal from "./components/Modal";
import ScheduleGrid from "./components/ScheduleGrid";
import ScheduleList from "./components/ScheduleList";
import { CategoryEnum, EventTypeEnum, CategoryDisplayNames } from "./enums";
import { getCategoryColors } from "./components/categoryColorHelper";

function App() {
  const [data, setData] = useState(null); // schedule JSON fetched at runtime
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null); // selected event
  const [dayIndex, setDayIndex] = useState(0); // active day tab

  // If you still want optional override via env + fetch, reintroduce previous effect.

  // --- Helpers for schedule transformation ---
  // Parse an ISO string but IGNORE its timezone offset, treating the hh:mm:ss as local (Bratislava) wall clock time.
  const parseNaive = (iso) => {
    if (!iso) return null;
    // Expect format: YYYY-MM-DDTHH:MM:SS(+/-..)?
    const m = iso.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return new Date(iso); // fallback normal parse
    const [, Y, Mo, D, H, Mi, S] = m;
    return new Date(
      Number(Y),
      Number(Mo) - 1,
      Number(D),
      Number(H),
      Number(Mi),
      Number(S || "0"),
      0,
    );
  };

  // Build derived festival days based on data.days entries (naive parsing) and apply business rules
  const festivalDays = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map((d) => parseNaive(d.start)).sort((a, b) => a - b);
  }, [data]);

  const lastDayDate = festivalDays.length
    ? festivalDays[festivalDays.length - 1]
    : null;

  const computeDayStart = (date) => {
    if (!date) return null;
    // Friday (5) starts 14:00 else 10:00
    const startHour = date.getDay() === 5 ? 14 : 10;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      startHour,
      0,
      0,
      0,
    );
  };
  const computeDayEnd = (date) => {
    if (!date) return null;
    const isLast =
      lastDayDate && date.toDateString() === lastDayDate.toDateString();
    const endHour = isLast ? 17 : 21;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      endHour,
      0,
      0,
      0,
    );
  };

  const day = data?.days?.[dayIndex];
  const dayAnchorDate = festivalDays[dayIndex];
  const dayStart = computeDayStart(dayAnchorDate);
  const dayEnd = computeDayEnd(dayAnchorDate);

  // Sections = rooms of the selected day
  const sections = useMemo(
    () => day?.rooms?.map((r) => ({ id: r.id, name: r.name })) || [],
    [day],
  );

  // Build event index by ID once
  const eventsIndex = useMemo(
    () => new Map((data?.events || []).map((e) => [e.id, e])),
    [data],
  );

  // Events for the selected day (transform to ScheduleGrid event shape, parse times naive)
  const events = useMemo(() => {
    if (!day || !day.rooms) return [];
    const list = [];
    day.rooms.forEach((room) => {
      (room.events || []).forEach((eid) => {
        const raw = eventsIndex.get(eid);
        if (!raw) return;
        const start = parseNaive(raw.start);
        const end = parseNaive(raw.end);
        // Filter if outside computed day range (allow overlap)
        if (dayStart && end <= dayStart) return;
        if (dayEnd && start >= dayEnd) return;
        const enumCategoryName = CategoryEnum[raw.category] || "None";
        const localizedEnumCategory =
          CategoryDisplayNames[enumCategoryName] || enumCategoryName;
        const hasCustomCategoryTitle = !!(
          raw.categoryTitle && raw.categoryTitle.trim().length > 0
        );
        const categoryName = hasCustomCategoryTitle
          ? raw.categoryTitle
          : localizedEnumCategory;
        const eventTypeName = hasCustomCategoryTitle
          ? undefined
          : EventTypeEnum?.[raw.type];
        const colors = getCategoryColors(raw.category);
        list.push({
          id: raw.id,
          title: raw.title,
          startTime: start,
          endTime: end,
          sectionId: room.id,
          category: raw.category,
          categoryLabel: categoryName !== "None" ? categoryName : undefined,
          speaker: raw.speaker,
          eventTypeLabel:
            eventTypeName && eventTypeName !== "None"
              ? eventTypeName
              : undefined,
          color: colors.bg,
          foregroundColor: colors.fg,
          borderColor: colors.border,
          headerColor: colors.border,
          // Header text should be white for Movie (2) and ProContest (9) regardless of body text color
          headerTextColor:
            raw.category === 2 || raw.category === 9 ? "#ffffff" : colors.fg,
          // HTML annotation content (rendered as HTML in modal)
          annotation: raw.annotation || null,
        });
      });
    });
    return list.sort((a, b) => a.startTime - b.startTime);
  }, [day, eventsIndex, dayStart, dayEnd]);

  // Responsive breakpoint detection (SSR-safe)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const compute = () => setIsMobile(window.innerWidth < 768);
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Runtime fetch for schedule (public folder). Allows updating JSON without rebuild.
  useEffect(() => {
    let cancelled = false;
    const url =
      window.__SCHEDULE_URL || process.env.PUBLIC_URL + "/schedule.json";
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data)
    return (
      <div style={{ padding: 16 }}>
        {loadError
          ? `Chyba načítania programu: ${loadError}`
          : "Loading schedule..."}
      </div>
    );

  return (
    <div style={{ padding: 8 }}>
      {festivalDays.length > 0 && (
        <div
          style={{ marginBottom: 8, display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {festivalDays.map((d, idx) => {
            const weekday = d.toLocaleDateString("sk-SK", { weekday: "long" });
            const capWeekday =
              weekday.charAt(0).toUpperCase() + weekday.slice(1);
            const dayNum = d.getDate();
            const monthShort = d.toLocaleDateString("sk-SK", {
              month: "short",
            });
            const label = `${capWeekday} ${dayNum}. ${monthShort}`;
            return (
              <button
                key={idx}
                onClick={() => {
                  setDayIndex(idx);
                  setSelected(null);
                }}
                style={{
                  padding: "4px 10px",
                  borderRadius: 4,
                  cursor: "pointer",
                  border:
                    idx === dayIndex ? "2px solid #0d6efd" : "1px solid #ccc",
                  background: idx === dayIndex ? "#e7f1ff" : "#f8f9fa",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {isMobile ? (
        <ScheduleList
          sections={sections}
          events={events}
          onEventSelect={setSelected}
        />
      ) : (
        <ScheduleGrid
          dayStart={dayStart}
          dayEnd={dayEnd}
          sections={sections}
          events={events}
          slotMinutes={15}
          slotHeightPx={28}
          timeLabelStepMinutes={30}
          timeColumnWidthPx={70}
          defaultEventColor={undefined}
          onEventSelect={setSelected}
        />
      )}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={(() => {
          if (!selected) return "";
          const cat = selected.categoryLabel || "";
          const type = selected.eventTypeLabel || "";
          if (selected.categoryLabel && selected.eventTypeLabel)
            return `${cat} - ${type}`;
          return cat || type || "";
        })()}
      >
        {selected && (
          <div>
            <h4 style={{ margin: "0 0 12px", fontSize: 16 }}>
              {selected.title}
            </h4>
            {(() => {
              const sectionName = sections.find(
                (s) => s.id === selected.sectionId,
              )?.name;
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    🕒{" "}
                    {selected.startTime.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                    {" – "}
                    {selected.endTime.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                    {sectionName && (
                      <span style={{ marginLeft: 12 }}> 📍 {sectionName}</span>
                    )}
                  </div>
                  {selected.speaker && (
                    <div style={{ fontWeight: 600 }}>👤 {selected.speaker}</div>
                  )}
                </div>
              );
            })()}
            {selected.annotation && selected.annotation.trim().length > 0 && (
              <div
                className="modal-section"
                style={{ fontSize: 14, lineHeight: 1.4 }}
                dangerouslySetInnerHTML={{ __html: selected.annotation.trim() }}
              />
            )}
            {console.log("selected", selected)}
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button
                onClick={() => setSelected(null)}
                style={{
                  padding: "6px 14px",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  background: "#f8f9fa",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default App;
